// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Sentry Error Tracking and Performance Monitoring
 *
 * This module configures and initializes Sentry for the Password Manager application.
 * It provides error capture, performance monitoring, and session replay capabilities.
 *
 * IMPORTANT: This module includes defensive checks to prevent "Object has no method 'updateFrom'"
 * TypeError that can occur in Sentry's internal polling mechanism. The replay integration is
 * properly initialized with all required methods before Sentry.init() is called.
 *
 * IMPORTANT: TanStack Router integration is NOT used to prevent "Cannot read properties of
 * undefined (reading 'matchRoutes')" error (Sentry #81784453). The router instance is not
 * available during Sentry initialization, causing timing issues. Use browserTracingIntegration
 * instead for performance monitoring.
 *
 * @module lib/sentry
 */

import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENABLED = import.meta.env.VITE_SENTRY_ENABLED !== "false";
const SENTRY_ENVIRONMENT = import.meta.env.SENTRY_ENVIRONMENT || "development";
const SENTRY_RELEASE = import.meta.env.SENTRY_RELEASE;

/**
 * Field names that must NEVER appear in Sentry events. Matched
 * case-insensitively against keys in breadcrumb `data` and
 * event-payload `extra`.
 */
export const SENSITIVE_FIELD_PATTERN = /token|password|secret|session|key|masterkey|authorization/i;

/**
 * String values that look token-shaped (long hex / base64 / JWT-like).
 * Used to detect leaked tokens that ended up inside a free-form string.
 */
export const TOKEN_LIKE_VALUE_PATTERN =
  /\b(?:eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|[A-Fa-f0-9]{40,}|[A-Za-z0-9+/=_-]{60,})\b/g;

/**
 * Recursively scrubs a breadcrumb so token / key / password values never
 * leave the device. Exported for the regression test.
 */
export function scrubBreadcrumbForSentry<T extends { data?: unknown; message?: unknown }>(
  bc: T,
): T {
  const next: { data?: unknown; message?: unknown } = { ...bc };
  if (next.data && typeof next.data === "object") {
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(next.data as Record<string, unknown>)) {
      if (SENSITIVE_FIELD_PATTERN.test(k)) {
        cleaned[k] = "[scrubbed]";
      } else if (typeof v === "string") {
        cleaned[k] = v.replace(TOKEN_LIKE_VALUE_PATTERN, "[scrubbed]");
      } else {
        cleaned[k] = v;
      }
    }
    next.data = cleaned;
  }
  if (typeof next.message === "string") {
    next.message = next.message.replace(TOKEN_LIKE_VALUE_PATTERN, "[scrubbed]");
  }
  return next as T;
}

/**
 * Initializes Sentry SDK with error tracking, performance monitoring, and session replay.
 *
 * This function should be called once at application startup, before any React components render.
 * It sets up browser tracing and session replay WITHOUT TanStack Router integration to avoid
 * timing dependency issues (router is not available during Sentry initialization).
 *
 * IMPORTANT: Does NOT use tanstackRouterIntegration to prevent "matchRoutes" TypeError (Sentry #81784453)
 *
 * Configuration is read from environment variables:
 * - VITE_SENTRY_DSN: Data Source Name for error reporting
 * - VITE_SENTRY_ENABLED: Enable/disable Sentry (defaults to true)
 * - SENTRY_ENVIRONMENT: Environment name (development, production)
 * - SENTRY_RELEASE: Release version for tracking
 *
 * @example
 * ```typescript
 * // In main.tsx
 * import { initSentry } from '@/lib/sentry';
 *
 * initSentry(); // Initialize before app renders (BEFORE router is created)
 * ```
 */
export function initSentry() {
  if (!SENTRY_DSN || !SENTRY_ENABLED) {
    console.info("[Sentry] Disabled - DSN not configured or explicitly disabled");
    return;
  }

  try {
    // Create integrations BEFORE init to ensure proper instantiation
    // This prevents "Object has no method 'updateFrom'" errors in Sentry's internal polling
    const browserTracing = Sentry.browserTracingIntegration();
    const replay = Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: true, // Mask form inputs by default
    });

    // NOTE: TanStack Router integration is NOT used because:
    // 1. Router instance is not available during Sentry initialization (main.tsx loads Sentry BEFORE router)
    // 2. Causes "TypeError: Cannot read properties of undefined (reading 'matchRoutes')" error (Sentry #81784453)
    // 3. browserTracingIntegration provides sufficient performance tracking without router coupling
    // 4. The router is initialized later in the React component tree, creating a timing dependency issue
    //
    // DO NOT add: Sentry.tanstackRouterIntegration({ router })
    // This will break the application with a matchRoutes error

    // Validate replay integration has required methods (defensive check)
    if (typeof replay !== "object" || replay === null) {
      console.error("[Sentry] Replay integration failed to initialize - not an object");
      // Continue without replay
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: SENTRY_ENVIRONMENT,
        release: SENTRY_RELEASE,
        integrations: [browserTracing],
        tracesSampleRate: SENTRY_ENVIRONMENT === "production" ? 0.1 : 1.0,
      });
      return;
    }

    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE,

      // Integrations - pre-instantiated to ensure proper object structure
      integrations: [browserTracing, replay],

      // Performance Monitoring
      tracesSampleRate: SENTRY_ENVIRONMENT === "production" ? 0.1 : 1.0,

      // Session Replay
      // Reduced dev sample rate to prevent 429 rate limit errors
      replaysSessionSampleRate: SENTRY_ENVIRONMENT === "production" ? 0.1 : 0.1,
      replaysOnErrorSampleRate: 1.0, // Always capture replays on errors

      // Filter events
      beforeSend(event, hint) {
        // Filter out specific errors
        const error = hint.originalException;

        // Ignore network errors in development
        if (SENTRY_ENVIRONMENT === "development") {
          if (error instanceof Error && error.message.includes("Failed to fetch")) {
            return null;
          }
        }

        // Filter out Sentry internal errors to prevent error loops
        if (error instanceof Error && error.message.includes("updateFrom")) {
          console.warn("[Sentry] Filtered internal updateFrom error to prevent loop");
          return null;
        }

        // Filter out Sentry replay session expiration errors (console noise)
        if (error instanceof Error) {
          const message = error.message.toLowerCase();
          if (message.includes("replay") && message.includes("session expired")) {
            return null;
          }
          if (message.includes("finish replay event")) {
            return null;
          }
        }

        // Sanitize sensitive data — headers, breadcrumbs, and any raw
        // strings that look like a session/token/key. This is the last
        // line of defense for the zero-knowledge model.
        if (event.request?.headers) {
          delete event.request.headers["Authorization"];
          delete event.request.headers["authorization"];
          delete event.request.headers["Cookie"];
          delete event.request.headers["cookie"];
        }

        if (Array.isArray(event.breadcrumbs)) {
          event.breadcrumbs = event.breadcrumbs.map((bc) =>
            scrubBreadcrumbForSentry(bc),
          );
        }

        return event;
      },

      // Enable debug mode in development
      debug: SENTRY_ENVIRONMENT === "development",

      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Network errors that are expected
        "Network request failed",
        "Load failed",
        // Sentry internal errors to prevent loops
        "updateFrom",
        "Object [object Object] has no method 'updateFrom'",
        // Sentry replay session errors (console noise only)
        "Cannot finish replay event",
        "Attempting to finish replay event after session expired",
        "replay session expired",
        /replay.*session.*expired/i,
        // Sentry transport rate limiting (429 errors)
        "TransportStatusCodeError",
        /Transport returned status code 429/i,
      ],

      // Deny URLs (usually third-party scripts)
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
      ],
    });

    console.info("[Sentry] Initialized successfully", {
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE,
      integrations: ["BrowserTracing", "Replay"],
    });
  } catch (error) {
    console.error("[Sentry] Initialization failed:", error);
    // Don't throw - allow app to continue without Sentry
  }
}

// Export Sentry for manual usage
export { Sentry };

/**
 * Sets the user context for Sentry error tracking.
 *
 * User context is attached to all subsequent error events, making it easier to
 * identify which users are experiencing issues. This is automatically called on
 * login/logout but can be used manually when needed.
 *
 * @param user - User information to track, or null to clear user context
 * @param user.id - Unique user identifier (required)
 * @param user.email - User email address (optional)
 * @param user.name - User display name (optional)
 *
 * @example
 * ```typescript
 * // On login
 * setSentryUser({
 *   id: user.id,
 *   email: user.email,
 *   name: user.name,
 * });
 *
 * // On logout
 * setSentryUser(null);
 * ```
 */
export function setSentryUser(user: { id: string; email?: string; name?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Adds a breadcrumb to the Sentry event trail.
 *
 * Breadcrumbs help debug issues by showing what happened leading up to an error.
 * They appear in the Sentry issue details and session replays.
 *
 * @param category - Breadcrumb category (e.g., "user_action", "navigation", "api")
 * @param message - Human-readable message describing what happened
 * @param level - Severity level (info, warning, error, debug) - defaults to "info"
 * @param data - Additional structured data to attach to breadcrumb
 *
 * @example
 * ```typescript
 * // User action
 * addBreadcrumb("user_action", "Clicked upload button", "info", {
 *   fileSize: file.size,
 *   fileType: file.type,
 * });
 *
 * // Navigation
 * addBreadcrumb("navigation", "Navigated to dashboard", "info");
 *
 * // API call
 * addBreadcrumb("api", "Fetched transcripts", "info", {
 *   count: transcripts.length,
 *   duration: 250,
 * });
 * ```
 */
export function addBreadcrumb(
  category: string,
  message: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Captures an error and sends it to Sentry with optional context.
 *
 * Use this in try-catch blocks to report unexpected errors. The error will be
 * sent to Sentry with user context, breadcrumbs, and any additional context provided.
 *
 * @param error - Error object to capture
 * @param context - Additional context data to attach to the error event
 *
 * @example
 * ```typescript
 * try {
 *   await uploadVideo(file);
 * } catch (error) {
 *   captureError(error instanceof Error ? error : new Error(String(error)), {
 *     operation: "video_upload",
 *     fileSize: file.size,
 *     userId: user?.id,
 *   });
 *   // Show user-friendly error message
 *   toast.error("Failed to upload video. Please try again.");
 * }
 * ```
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>
) {
  Sentry.captureException(error, {
    extra: context,
  });
}
